// Tenant-timezone day and clock helpers for the availability panel.
//
// The band RPC answers in TENANT-local days (get_service_booking_window's
// day_zero and the gates all read the tenant zone), and a guest abroad must
// see venue times. The shared windowsForDate / hoursPillText work in the
// browser's zone (packages/shared/src/booking-core/service-hours.ts), so the
// panel uses these instead. Pure (no React, no config.ts): the logic spec
// imports them under Node. The React clock lives in use-now.ts.

import type { ServiceHoursRow } from "@vertu/shared";
import { tzOffsetMinutes } from "./chidesk-availability";
import { addDaysYmd, weekdayOfYmd, type OffsetAt } from "./availability-logic";

export const DEFAULT_TZ = "Africa/Johannesburg";

export function tenantTz(tenant: { timezone?: string | null } | null | undefined): string {
  const tz = tenant?.timezone;
  if (!tz) return DEFAULT_TZ;
  try {
    new Intl.DateTimeFormat("en", { timeZone: tz });
    return tz;
  } catch {
    return DEFAULT_TZ;
  }
}

export function offsetMinAt(tz: string): OffsetAt {
  return (ms: number) => tzOffsetMinutes(tz, new Date(ms));
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    formatters.set(tz, f);
  }
  return f;
}

function parts(ms: number, tz: string): Record<string, string> {
  const p: Record<string, string> = {};
  for (const part of partsFormatter(tz).formatToParts(new Date(ms))) p[part.type] = part.value;
  return p;
}

/** The tenant-local calendar day of an instant ("YYYY-MM-DD"). */
export function ymdOfInstant(ms: number, tz: string): string {
  const p = parts(ms, tz);
  return `${p.year}-${p.month}-${p.day}`;
}

export function todayYmdIn(tz: string, nowMs: number = Date.now()): string {
  return ymdOfInstant(nowMs, tz);
}

/** "HH:MM", 24-hour, in the tenant zone. */
export function formatHm(ms: number, tz: string): string {
  const p = parts(ms, tz);
  const hour = p.hour === "24" ? "00" : p.hour;
  return `${hour}:${p.minute}`;
}

const zoneNameFormatters = new Map<string, Intl.DateTimeFormat>();

/** The zone's short name at an instant ("BST", "GMT", "GMT+2"). */
export function zoneAbbrev(ms: number, tz: string): string {
  let f = zoneNameFormatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", { timeZone: tz, timeZoneName: "short" });
    zoneNameFormatters.set(tz, f);
  }
  return f.formatToParts(new Date(ms)).find((p) => p.type === "timeZoneName")?.value ?? "";
}

/**
 * Whether this instant's wall-clock time also happens at another instant of
 * the same tenant day — the repeated hour of a DST fall-back night, when
 * "01:00" names two different starts.
 */
export function wallTimeRepeats(ms: number, tz: string): boolean {
  const hm = formatHm(ms, tz);
  const day = ymdOfInstant(ms, tz);
  for (const shift of [-60, -30, 30, 60]) {
    const other = ms + shift * 60_000;
    if (formatHm(other, tz) === hm && ymdOfInstant(other, tz) === day) return true;
  }
  return false;
}

/** "HH:MM", with the zone's short name when that wall time happens twice that night ("01:00 BST"). */
export function formatHmUnambiguous(ms: number, tz: string): string {
  const hm = formatHm(ms, tz);
  if (!wallTimeRepeats(ms, tz)) return hm;
  const zone = zoneAbbrev(ms, tz);
  return zone ? `${hm} ${zone}` : hm;
}

/**
 * A browser-local Date (midday) on the calendar day `ymd`. For the shared
 * helpers that read a Date's browser-local weekday (hoursPillText,
 * windowsForDate): handing them the TENANT day this way makes them answer
 * for the venue's weekday wherever the guest is.
 */
export function browserDateOfYmd(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y!, m! - 1, d!, 12, 0, 0, 0);
}

/** The instant a tenant-local wall-clock minute of `ymd` happens. */
export function zonedWallMs(ymd: string, minutesOfDay: number, tz: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  const guess = Date.UTC(y!, m! - 1, d!) + minutesOfDay * 60_000;
  const first = guess - tzOffsetMinutes(tz, new Date(guess)) * 60_000;
  // One correction pass: the offset at the real instant can differ from the
  // offset at the naive guess across a DST change.
  return guess - tzOffsetMinutes(tz, new Date(first)) * 60_000;
}

export function zonedMidnightMs(ymd: string, tz: string): number {
  return zonedWallMs(ymd, 0, tz);
}

/** Midday of a tenant day — an instant that is on that day in every browser within ±12 h. */
export function zonedNoonIso(ymd: string, tz: string): string {
  return new Date(zonedWallMs(ymd, 12 * 60, tz)).toISOString();
}

function clockMinutes(t: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/**
 * The day's open windows in the TENANT zone. A window that closes at or
 * before it opens crosses midnight (its close is on the next day).
 */
export function zonedDayWindows(
  hours: readonly ServiceHoursRow[],
  ymd: string,
  tz: string,
): { startMs: number; endMs: number }[] {
  const dow = weekdayOfYmd(ymd);
  const out: { startMs: number; endMs: number }[] = [];
  for (const row of hours) {
    if (row.day_of_week !== dow || row.is_closed || !row.opens_at || !row.closes_at) continue;
    const om = clockMinutes(row.opens_at);
    const cm = clockMinutes(row.closes_at);
    if (om == null || cm == null) continue;
    const startMs = zonedWallMs(ymd, om, tz);
    const endMs = cm > om ? zonedWallMs(ymd, cm, tz) : zonedWallMs(addDaysYmd(ymd, 1), cm, tz);
    out.push({ startMs, endMs });
  }
  return out.sort((a, b) => a.startMs - b.startMs);
}

// ─── Panel lines in the cart ─────────────────────────────────────────────────

/**
 * The venue zone an availability-panel cart line is shown in (its metadata
 * carries option_led and the tenant_tz it was held in). The panel shows starts
 * on the venue's clock and holds them there, so the cart must say the same
 * time to a guest browsing from another zone. undefined for every other line
 * (they keep the browser's clock) and for a zone this browser cannot use.
 */
export function panelLineZone(metadata: Record<string, unknown> | null | undefined): string | undefined {
  if (metadata?.option_led !== true) return undefined;
  const tz = metadata.tenant_tz;
  if (typeof tz !== "string" || !tz) return undefined;
  try {
    new Intl.DateTimeFormat("en", { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

/** An `<input type="datetime-local">` value ("YYYY-MM-DDTHH:MM") on the zone's clock. */
export function zonedInputValue(iso: string, tz: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  return `${ymdOfInstant(ms, tz)}T${formatHm(ms, tz)}`;
}

/** The instant a zone-clock "YYYY-MM-DDTHH:MM" names (NaN when unparseable). */
export function zonedInputMs(value: string, tz: string): number {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return Number.NaN;
  return zonedWallMs(m[1]!, Number(m[2]) * 60 + Number(m[3]), tz);
}
